import { type ReactNode, useCallback, useRef, useState } from 'react';
import { type Schema } from 'ts-json-schema-generator';
import { type PopupProps } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { type OpenApiSchemaSectionTemplate } from '#shared/api/schema';
import { useYandexKit } from '#shared/components/YandexKitProvider';
import { constructorProductLoggingEvents } from '../lib/constructorProductLoggingEvents';
import { addConstructorSection, useConstructorPageSettings, useConstructorPreviewPage } from '../model/slices';
import { useChangeConstructorRightBarSection } from './useChangeConstructorRightBarSection';
import { useConstructorCanAddSections } from './useConstructorCanAddSections';

export const useConstructorAddSectionPopup = ({
    renderAddSectionPopup,
    getDefaultModulesSettings,
}: {
    renderAddSectionPopup: (
        props: PopupProps & {
            onClick: (props: {
                widget: string;
                newDisplaySequence: number;
                template?: OpenApiSchemaSectionTemplate;
            }) => void;
            widgetToReplace?: string;
            newDisplaySequence: number;
        }
    ) => ReactNode;
    getDefaultModulesSettings: (schema: Schema) => Object;
}) => {
    const [newDisplaySequence, setNewDisplaySequence] = useState(0);
    const [widgetToReplace, setWidgetToReplace] = useState<string | undefined>();
    const {
        value: isAddSectionPopupOpen,
        toggleOn: openAddSectionPopup,
        toggleOff: closeAddSectionPopup,
    } = useToggle(false);

    const addCallbackRef = useRef<((settings: Object) => void) | undefined>();
    const currentPageId = useConstructorPreviewPage();
    const page = useConstructorPageSettings(currentPageId);
    const canAddSections = useConstructorCanAddSections(page?.alias);

    const openPopup = useCallback(
        ({
            newDisplaySequence,
            widgetToReplace,
            addCallback,
        }: {
            newDisplaySequence: number;
            widgetToReplace?: string;
            addCallback?: (settings: Object) => void;
        }) => {
            if (!addCallback && !canAddSections) {
                return;
            }

            setNewDisplaySequence(newDisplaySequence);
            setWidgetToReplace(widgetToReplace);
            addCallbackRef.current = addCallback;
            openAddSectionPopup();
        },
        [openAddSectionPopup, canAddSections]
    );

    const cleanUp = useCallback(() => {
        addCallbackRef.current = undefined;
        setWidgetToReplace(undefined);
    }, []);

    const addSectionPopupRef = useRef<HTMLDivElement>(null);

    const yandexKit = useYandexKit();

    const changeConstructorRightBarSection = useChangeConstructorRightBarSection();

    const addSectionHandle = useCallback(
        ({
            widget,
            newDisplaySequence,
            template,
        }: {
            widget: string;
            newDisplaySequence: number;
            template?: OpenApiSchemaSectionTemplate;
        }) => {
            closeAddSectionPopup();

            if (!addCallbackRef.current && !canAddSections) {
                cleanUp();

                return;
            }

            const schema = yandexKit.getSettings({
                widget,
                template,
            });

            if (!schema) {
                return;
            }

            let settings = {};

            const modulesSettings = schema?.properties?.modules;

            if (typeof modulesSettings === 'object') {
                if (modulesSettings.type === 'array') {
                    settings = {
                        modules: [],
                    };
                } else if (modulesSettings.type === 'object') {
                    settings = getDefaultModulesSettings(modulesSettings);
                }
            }

            const migratedSettings = yandexKit.migrateSectionSettings
                ? yandexKit.migrateSectionSettings(widget, settings)
                : settings;

            const finalSettings = {
                widget,
                settings: yandexKit.applyPlaceholders
                    ? yandexKit.applyPlaceholders(widget, migratedSettings)
                    : migratedSettings,
                display_sequence: newDisplaySequence,
                template_id: template?.id,
                id: self.crypto.randomUUID(),
            };

            if (addCallbackRef.current) {
                addCallbackRef.current(finalSettings);
            } else {
                logEventString(constructorProductLoggingEvents.Section.Add, 'true', { widget });

                const newInstanceId = addConstructorSection(
                    currentPageId,
                    finalSettings,
                    schema?.totalMaximum === 1 || schema?.alwaysComponent === true
                );

                changeConstructorRightBarSection(newInstanceId);
            }

            cleanUp();
        },
        [
            yandexKit,
            currentPageId,
            canAddSections,
            closeAddSectionPopup,
            getDefaultModulesSettings,
            cleanUp,
            changeConstructorRightBarSection,
        ]
    );

    return {
        openPopup,
        renderPopup: () =>
            renderAddSectionPopup({
                isOpen: isAddSectionPopupOpen,
                triggerRef: addSectionPopupRef,
                onClose: closeAddSectionPopup,
                isDismissable: true,
                placement: 'right',
                onClick: addSectionHandle,
                newDisplaySequence,
                widgetToReplace,
            }),
        addSectionPopupRef,
        closePopup: closeAddSectionPopup,
    };
};
